/**
 * Demo Data Service
 * Generates realistic demo data for the Asset Audit module
 * Persists data to localStorage for demo mode
 */

import type {
  Audit,
  AuditException,
  AuditActivityLog,
  RecurrencePattern,
} from '../../types/asset-audit';
import {
  AuditStatus,
  AuditType,
  AuditPriority,
  AuditScope,
  AssigneeStatus,
  ExceptionType,
  ExceptionSeverity,
  ExceptionStatus,
  ExceptionPriority,
  AssetCondition,
  AuditActivityType,
  RecurrenceFrequency,
} from '../../types/asset-audit';

const STORAGE_KEY = 'audit_demo_data';
const DEMO_VERSION = '1.3.0'; // Fixed audit IDs to match mobile app

// Helper function to get assignee status based on audit status - extracted to fix nested ternary (S3358)
function getAssigneeStatusFromAuditStatus(auditStatus: AuditStatus): AssigneeStatus {
  if (auditStatus === AuditStatus.Assigned) {
    return AssigneeStatus.Pending;
  }
  if (auditStatus === AuditStatus.InProgress) {
    return AssigneeStatus.InProgress;
  }
  if (auditStatus === AuditStatus.Completed) {
    return AssigneeStatus.Completed;
  }
  return AssigneeStatus.Pending;
}

// Helper function to get recurrence occurrences based on frequency - extracted to fix nested ternary (S3358)
function getRecurrenceOccurrences(frequency: RecurrenceFrequency): number | undefined {
  if (frequency === RecurrenceFrequency.Monthly) {
    return 12;
  }
  if (frequency === RecurrenceFrequency.Quarterly) {
    return 4;
  }
  return undefined;
}

// Demo Users
export interface DemoUser {
  id: string;
  name: string;
  email: string;
  role: string;
  department: string;
  avatar?: string;
  currentWorkload: number; // Number of active audits
  availability: 'available' | 'busy' | 'away';
}

// Demo Assets
export interface DemoAsset {
  id: string;
  code: string;
  name: string;
  category: string;
  location: string;
  department: string;
  condition: AssetCondition;
  lastAuditDate?: Date;
  value: number;
}

interface DemoData {
  version: string;
  users: DemoUser[];
  assets: DemoAsset[];
  audits: Audit[];
  exceptions: AuditException[];
  activityLogs: AuditActivityLog[];
  lastGenerated: Date;
}

class DemoDataService {
  private data: DemoData | null = null;

  /**
   * Initialize demo data - load from storage or generate new
   */
  initialize(): DemoData {
    console.log('[DemoDataService] Initializing demo data...');
    const stored = this.loadFromStorage();
    
    if (stored && stored.version === DEMO_VERSION) {
      console.log('[DemoDataService] Loaded existing demo data from storage:', {
        audits: stored.audits.length,
        users: stored.users.length,
        assets: stored.assets.length,
        exceptions: stored.exceptions.length,
      });
      this.data = stored;
      return stored;
    }

    // Generate fresh demo data
    console.log('[DemoDataService] Generating fresh demo data...');
    this.data = this.generateDemoData();
    this.saveToStorage();
    console.log('[DemoDataService] Demo data generated and saved:', {
      audits: this.data.audits.length,
      users: this.data.users.length,
      assets: this.data.assets.length,
      exceptions: this.data.exceptions.length,
    });
    return this.data;
  }

  /**
   * Get current demo data
   */
  getData(): DemoData {
    if (!this.data) {
      return this.initialize();
    }
    return this.data;
  }

  /**
   * Reset demo data to initial state
   */
  reset(): DemoData {
    this.data = this.generateDemoData();
    this.saveToStorage();
    return this.data;
  }

  /**
   * Update demo data and persist
   */
  updateData(updates: Partial<DemoData>): void {
    if (!this.data) {
      this.initialize();
    }
    this.data = { ...this.data!, ...updates };
    this.saveToStorage();
  }

  /**
   * Generate complete demo data set
   */
  private generateDemoData(): DemoData {
    const users = this.generateUsers();
    const assets = this.generateAssets();
    const audits = this.generateAudits(users, assets);
    const exceptions = this.generateExceptions(audits, assets, users);
    const activityLogs = this.generateActivityLogs(audits, users);

    return {
      version: DEMO_VERSION,
      users,
      assets,
      audits,
      exceptions,
      activityLogs,
      lastGenerated: new Date(),
    };
  }

  /**
   * Generate 10 demo users with varied roles
   */
  private generateUsers(): DemoUser[] {
    const roles = ['Asset Manager', 'Auditor', 'Supervisor', 'Technician', 'Administrator'];
    const departments = ['Operations', 'Facilities', 'IT', 'Finance', 'Maintenance'];
    const firstNames = ['John', 'Sarah', 'Michael', 'Emily', 'David', 'Jessica', 'Robert', 'Lisa', 'James', 'Maria'];
    const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis', 'Rodriguez', 'Martinez'];

    return Array.from({ length: 10 }, (_, i) => ({
      id: `user-${i + 1}`,
      name: `${firstNames[i]} ${lastNames[i]}`,
      email: `${firstNames[i].toLowerCase()}.${lastNames[i].toLowerCase()}@company.com`,
      role: roles[i % roles.length],
      department: departments[i % departments.length],
      avatar: `https://i.pravatar.cc/150?img=${i + 1}`,
      currentWorkload: Math.floor(Math.random() * 8),
      availability: ['available', 'busy', 'away'][Math.floor(Math.random() * 3)] as any,
    }));
  }

  /**
   * Generate 600+ demo assets
   */
  private generateAssets(): DemoAsset[] {
    const categories = [
      'Computer Equipment',
      'Furniture',
      'Vehicles',
      'Machinery',
      'Office Equipment',
      'Tools',
      'Medical Equipment',
      'Laboratory Equipment',
    ];
    const locations = [
      'Building A - Floor 1',
      'Building A - Floor 2',
      'Building A - Floor 3',
      'Building B - Floor 1',
      'Building B - Floor 2',
      'Warehouse',
      'Parking Lot',
      'Lab 1',
      'Lab 2',
      'Workshop',
    ];
    const departments = ['Operations', 'Facilities', 'IT', 'Finance', 'Maintenance', 'Research', 'HR'];
    const conditions: AssetCondition[] = [
      AssetCondition.Excellent,
      AssetCondition.Good,
      AssetCondition.Fair,
      AssetCondition.Poor,
      AssetCondition.Critical,
    ];

    return Array.from({ length: 650 }, (_, i) => {
      const category = categories[i % categories.length];
      const assetNumber = String(i + 1).padStart(6, '0');
      
      return {
        id: String(i + 1),
        code: `AST-${assetNumber}`,
        name: `${category} ${assetNumber}`,
        category,
        location: locations[Math.floor(Math.random() * locations.length)],
        department: departments[Math.floor(Math.random() * departments.length)],
        condition: conditions[Math.floor(Math.random() * conditions.length)],
        lastAuditDate: this.randomPastDate(90),
        value: Math.floor(Math.random() * 50000) + 500,
      };
    });
  }

  /**
   * Generate audits: 24 active, 12 scheduled, 156 historical
   */
  private generateAudits(users: DemoUser[], assets: DemoAsset[]): Audit[] {
    const audits: Audit[] = [];
    let auditNumber = 1000;

    // Add mobile app demo audits first
    audits.push(...this.createMobileAppAudits(users, assets));
    auditNumber += 6; // Skip 6 numbers for the mobile app audits

    // Generate remaining active audits (24 - 6 = 18)
    for (let i = 0; i < 18; i++) {
      audits.push(this.createAudit(auditNumber++, 'active', users, assets));
    }

    // Generate 12 scheduled audits
    for (let i = 0; i < 12; i++) {
      audits.push(this.createAudit(auditNumber++, 'scheduled', users, assets));
    }

    // Generate 156 historical audits
    for (let i = 0; i < 156; i++) {
      audits.push(this.createAudit(auditNumber++, 'historical', users, assets));
    }

    return audits;
  }

  /**
   * Create mobile app demo audits
   */
  private createMobileAppAudits(users: DemoUser[], assets: DemoAsset[]): Audit[] {
    const now = new Date();
    const baseUser = users[0];

    return [
      // 1. London HQ IT Equipment - In Progress
      {
        id: 'AUD-2025-001',
        auditNumber: 'AUD-2025-001',
        title: 'London HQ IT Equipment Audit',
        description: 'Quarterly audit of all IT equipment including laptops, monitors, and peripherals',
        type: AuditType.FullInventory,
        status: AuditStatus.InProgress,
        priority: AuditPriority.High,
        scope: AuditScope.Locations,
        scopeDetails: {
          locations: ['London HQ - Floor 3', 'London HQ - Floor 4'],
          categories: ['Computers', 'Monitors', 'Peripherals'],
          assetIds: assets.slice(0, 50).map(a => a.id),
        },
        assignedUsers: [
          {
            userId: users[0].id,
            userName: users[0].name,
            userEmail: users[0].email,
            userAvatar: users[0].avatar,
            assignedDate: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
            status: AssigneeStatus.InProgress,
            completedAssets: 40,
            totalAssets: 50,
            notificationSent: true,
          },
        ],
        scheduledStartDate: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        scheduledEndDate: new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString(),
        actualStartDate: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        createdBy: baseUser.id,
        createdByName: baseUser.name,
        createdDate: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        lastModifiedDate: new Date().toISOString(),
        completionPercentage: 80,
        completedAssets: 40,
        pendingAssets: 10,
        totalAssets: 50,
        exceptionCount: 4,
        flaggedAssets: 3,
        missingAssets: 1,
        isRecurring: true,
        recurrencePattern: {
          frequency: RecurrenceFrequency.Quarterly,
          interval: 1,
          endDate: new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000).toISOString(),
        },
        isOverdue: false,
        daysRemaining: 2,
        allowPartialCompletion: true,
        requirePhotos: true,
        requireNotes: false,
        autoResolveExceptions: false,
      },
      // 2. Manchester Office Furniture - Pending
      {
        id: 'AUD-2025-002',
        auditNumber: 'AUD-2025-002',
        title: 'Manchester Office Furniture Verification',
        description: 'Annual verification of office furniture including desks, chairs, and filing cabinets',
        type: AuditType.LocationVerification,
        status: AuditStatus.Assigned,
        priority: AuditPriority.Medium,
        scope: AuditScope.Locations,
        scopeDetails: {
          locations: ['Manchester Office'],
          categories: ['Furniture'],
          assetIds: assets.slice(50, 90).map(a => a.id),
        },
        assignedUsers: [
          {
            userId: users[1].id,
            userName: users[1].name,
            userEmail: users[1].email,
            userAvatar: users[1].avatar,
            assignedDate: new Date().toISOString(),
            status: AssigneeStatus.Pending,
            completedAssets: 0,
            totalAssets: 40,
            notificationSent: true,
          },
        ],
        scheduledStartDate: new Date().toISOString(),
        scheduledEndDate: new Date(now.getTime() + 10 * 24 * 60 * 60 * 1000).toISOString(),
        createdBy: baseUser.id,
        createdByName: baseUser.name,
        createdDate: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        lastModifiedDate: new Date().toISOString(),
        completionPercentage: 0,
        completedAssets: 0,
        pendingAssets: 40,
        totalAssets: 40,
        exceptionCount: 0,
        isRecurring: false,
        isOverdue: false,
        daysRemaining: 10,
        allowPartialCompletion: true,
        requirePhotos: false,
        requireNotes: false,
        autoResolveExceptions: false,
      },
      // 3. Birmingham Warehouse - Pending Approval
      {
        id: 'AUD-2025-003',
        auditNumber: 'AUD-2025-003',
        title: 'Birmingham Warehouse Stock Check',
        description: 'Monthly stock verification for warehouse inventory',
        type: AuditType.Reconciliation,
        status: AuditStatus.UnderReview,
        priority: AuditPriority.High,
        scope: AuditScope.Locations,
        scopeDetails: {
          locations: ['Birmingham Warehouse'],
          categories: ['Inventory', 'Equipment'],
          assetIds: assets.slice(90, 120).map(a => a.id),
        },
        assignedUsers: [
          {
            userId: users[2].id,
            userName: users[2].name,
            userEmail: users[2].email,
            userAvatar: users[2].avatar,
            assignedDate: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
            status: AssigneeStatus.Completed,
            completedAssets: 30,
            totalAssets: 30,
            notificationSent: true,
          },
        ],
        scheduledStartDate: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        scheduledEndDate: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        actualStartDate: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
        actualEndDate: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        createdBy: baseUser.id,
        createdByName: baseUser.name,
        createdDate: new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000).toISOString(),
        lastModifiedDate: new Date().toISOString(),
        completionPercentage: 100,
        completedAssets: 30,
        pendingAssets: 0,
        totalAssets: 30,
        exceptionCount: 2,
        flaggedAssets: 1,
        missingAssets: 1,
        isRecurring: true,
        recurrencePattern: {
          frequency: RecurrenceFrequency.Monthly,
          interval: 1,
          endDate: new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000).toISOString(),
        },
        isOverdue: false,
        allowPartialCompletion: true,
        requirePhotos: true,
        requireNotes: true,
        autoResolveExceptions: false,
      },
      // 4. Edinburgh Computer Equipment - Completed
      {
        id: 'AUD-2025-004',
        auditNumber: 'AUD-2025-004',
        title: 'Edinburgh Office Computer Equipment',
        description: 'Bi-annual audit of computer equipment and accessories',
        type: AuditType.ConditionAssessment,
        status: AuditStatus.Completed,
        priority: AuditPriority.Medium,
        scope: AuditScope.Categories,
        scopeDetails: {
          locations: ['Edinburgh Office'],
          categories: ['Computers', 'Laptops'],
          assetIds: assets.slice(120, 145).map(a => a.id),
        },
        assignedUsers: [
          {
            userId: users[3].id,
            userName: users[3].name,
            userEmail: users[3].email,
            userAvatar: users[3].avatar,
            assignedDate: new Date(now.getTime() - 20 * 24 * 60 * 60 * 1000).toISOString(),
            status: AssigneeStatus.Completed,
            completedAssets: 25,
            totalAssets: 25,
            notificationSent: true,
          },
        ],
        scheduledStartDate: new Date(now.getTime() - 20 * 24 * 60 * 60 * 1000).toISOString(),
        scheduledEndDate: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString(),
        actualStartDate: new Date(now.getTime() - 20 * 24 * 60 * 60 * 1000).toISOString(),
        actualEndDate: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000).toISOString(),
        createdBy: baseUser.id,
        createdByName: baseUser.name,
        createdDate: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString(),
        lastModifiedDate: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000).toISOString(),
        completionPercentage: 100,
        completedAssets: 25,
        pendingAssets: 0,
        totalAssets: 25,
        exceptionCount: 0,
        isRecurring: false,
        isOverdue: false,
        allowPartialCompletion: true,
        requirePhotos: true,
        requireNotes: false,
        autoResolveExceptions: false,
      },
      // 5. Bristol Vehicle Inspection - In Progress
      {
        id: 'AUD-2025-005',
        auditNumber: 'AUD-2025-005',
        title: 'Bristol Office Ad-hoc Vehicle Inspection',
        description: 'Unscheduled inspection of company vehicles following reported damage',
        type: AuditType.SpotCheck,
        status: AuditStatus.InProgress,
        priority: AuditPriority.High,
        scope: AuditScope.Categories,
        scopeDetails: {
          locations: ['Bristol Office'],
          categories: ['Vehicles'],
          assetIds: assets.slice(145, 153).map(a => a.id),
        },
        assignedUsers: [
          {
            userId: users[4].id,
            userName: users[4].name,
            userEmail: users[4].email,
            userAvatar: users[4].avatar,
            assignedDate: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
            status: AssigneeStatus.InProgress,
            completedAssets: 5,
            totalAssets: 8,
            notificationSent: true,
          },
        ],
        scheduledStartDate: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        scheduledEndDate: new Date(now.getTime() + 1 * 24 * 60 * 60 * 1000).toISOString(),
        actualStartDate: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000).toISOString(),
        createdBy: baseUser.id,
        createdByName: baseUser.name,
        createdDate: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
        lastModifiedDate: new Date().toISOString(),
        completionPercentage: 63,
        completedAssets: 5,
        pendingAssets: 3,
        totalAssets: 8,
        exceptionCount: 2,
        flaggedAssets: 2,
        isRecurring: false,
        isOverdue: false,
        daysRemaining: 1,
        allowPartialCompletion: true,
        requirePhotos: true,
        requireNotes: true,
        autoResolveExceptions: false,
      },
      // 6. Leeds Distribution Center - Draft
      {
        id: 'AUD-2025-006',
        auditNumber: 'AUD-2025-006',
        title: 'Leeds Distribution Center Annual Audit',
        description: 'Comprehensive annual audit of all assets in the distribution center',
        type: AuditType.FullInventory,
        status: AuditStatus.Scheduled,
        priority: AuditPriority.Low,
        scope: AuditScope.Locations,
        scopeDetails: {
          locations: ['Leeds Distribution Center'],
          categories: ['All'],
          assetIds: assets.slice(153, 253).map(a => a.id),
        },
        assignedUsers: [
          {
            userId: users[0].id,
            userName: users[0].name,
            userEmail: users[0].email,
            userAvatar: users[0].avatar,
            assignedDate: new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000).toISOString(),
            status: AssigneeStatus.Pending,
            completedAssets: 0,
            totalAssets: 100,
            notificationSent: false,
          },
        ],
        scheduledStartDate: new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000).toISOString(),
        scheduledEndDate: new Date(now.getTime() + 45 * 24 * 60 * 60 * 1000).toISOString(),
        createdBy: baseUser.id,
        createdByName: baseUser.name,
        createdDate: new Date().toISOString(),
        lastModifiedDate: new Date().toISOString(),
        completionPercentage: 0,
        completedAssets: 0,
        pendingAssets: 100,
        totalAssets: 100,
        exceptionCount: 0,
        isRecurring: true,
        recurrencePattern: {
          frequency: RecurrenceFrequency.Yearly,
          interval: 1,
          endDate: new Date(now.getTime() + 1095 * 24 * 60 * 60 * 1000).toISOString(),
        },
        isOverdue: false,
        daysRemaining: 15,
        allowPartialCompletion: true,
        requirePhotos: true,
        requireNotes: false,
        autoResolveExceptions: false,
      },
    ];
  }

  /**
   * Create a single audit
   */
  private createAudit(
    auditNumber: number,
    category: 'active' | 'scheduled' | 'historical',
    users: DemoUser[],
    assets: DemoAsset[]
  ): Audit {
    const types: AuditType[] = [
      AuditType.FullInventory,
      AuditType.LocationVerification,
      AuditType.ConditionAssessment,
      AuditType.ComplianceCheck,
      AuditType.HighValueAssets,
      AuditType.Reconciliation,
      AuditType.SpotCheck
    ];
    const priorities: AuditPriority[] = [
      AuditPriority.Low,
      AuditPriority.Medium,
      AuditPriority.High,
      AuditPriority.Critical
    ];
    const scopes: AuditScope[] = [
      AuditScope.AllAssets,
      AuditScope.Locations,
      AuditScope.Departments,
      AuditScope.Categories,
      AuditScope.CustomList
    ];

    const type = types[Math.floor(Math.random() * types.length)];
    const priority = priorities[Math.floor(Math.random() * priorities.length)];
    const scope = scopes[Math.floor(Math.random() * scopes.length)];

    let status: AuditStatus;
    let startDate: Date;
    let endDate: Date;
    let completedDate: Date | undefined;
    let progress: number;

    if (category === 'active') {
      const statuses: AuditStatus[] = [AuditStatus.Assigned, AuditStatus.InProgress, AuditStatus.UnderReview, AuditStatus.OnHold];
      status = statuses[Math.floor(Math.random() * statuses.length)];
      startDate = this.randomPastDate(30);
      endDate = this.randomFutureDate(30);
      progress = Math.floor(Math.random() * 90) + 5; // 5-95%
    } else if (category === 'scheduled') {
      status = AuditStatus.Scheduled;
      startDate = this.randomFutureDate(60);
      endDate = new Date(startDate.getTime() + 14 * 24 * 60 * 60 * 1000); // 14 days later
      progress = 0;
    } else {
      const statuses: AuditStatus[] = [AuditStatus.Completed, AuditStatus.Cancelled];
      status = statuses[Math.floor(Math.random() * 10) < 9 ? 0 : 1]; // 90% completed, 10% cancelled
      startDate = this.randomPastDate(180, 60);
      endDate = new Date(startDate.getTime() + 14 * 24 * 60 * 60 * 1000);
      completedDate = status === AuditStatus.Completed ? this.randomDateBetween(startDate, endDate) : undefined;
      progress = status === AuditStatus.Completed ? 100 : Math.floor(Math.random() * 80);
    }

    // Select random assignees (1-3)
    const numAssignees = Math.floor(Math.random() * 3) + 1;
    const selectedUsers = this.shuffleArray([...users]).slice(0, numAssignees);
    const assignees = selectedUsers.map(user => ({
      userId: user.id,
      userName: user.name,
      userEmail: user.email,
      assignedDate: startDate,
      status: getAssigneeStatusFromAuditStatus(status),
      completedAssets: Math.floor((progress / 100) * 50),
      totalAssets: 50,
    }));

    // Select random assets for scope
    const numAssets = Math.floor(Math.random() * 100) + 20;
    const selectedAssets = this.shuffleArray([...assets]).slice(0, numAssets);
    const assetIds = selectedAssets.map(a => a.id);

    const audit: Audit = {
      id: `audit-${auditNumber}`,
      auditNumber: `AUD-${String(auditNumber).padStart(6, '0')}`,
      title: this.generateAuditTitle(type, scope),
      description: `${type.charAt(0).toUpperCase() + type.slice(1)} audit for ${scope} scope`,
      type,
      status,
      priority,
      scope,
      scopeDetails: {
        assetIds,
      },
      assignedUsers: assignees.map(a => ({
        ...a,
        assignedDate: a.assignedDate.toISOString(),
        notificationSent: true,
      })),
      scheduledStartDate: startDate.toISOString(),
      scheduledEndDate: endDate.toISOString(),
      actualStartDate: status !== AuditStatus.Scheduled ? startDate.toISOString() : undefined,
      actualEndDate: completedDate?.toISOString(),
      createdBy: users[0].id,
      createdByName: users[0].name,
      createdDate: this.randomPastDate(200).toISOString(),
      lastModifiedDate: new Date().toISOString(),
      completionPercentage: progress,
      completedAssets: Math.floor((progress / 100) * assetIds.length),
      pendingAssets: assetIds.length - Math.floor((progress / 100) * assetIds.length),
      totalAssets: assetIds.length,
      exceptionCount: category === 'active' ? Math.floor(Math.random() * 5) : 0,
      isRecurring: Math.random() > 0.7,
      recurrencePattern: Math.random() > 0.7 ? this.generateRecurrencePattern() : undefined,
      isOverdue: status !== AuditStatus.Completed && status !== AuditStatus.Cancelled && endDate < new Date(),
      allowPartialCompletion: true,
      requirePhotos: type === AuditType.FullInventory || type === AuditType.ConditionAssessment,
      requireNotes: false,
      autoResolveExceptions: false,
    };

    return audit;
  }

  /**
   * Generate audit title based on type and scope
   */
  private generateAuditTitle(type: AuditType, scope: AuditScope): string {
    const typeNames: Record<AuditType, string> = {
      [AuditType.FullInventory]: 'Full Inventory Audit',
      [AuditType.LocationVerification]: 'Location Verification',
      [AuditType.ConditionAssessment]: 'Condition Assessment',
      [AuditType.ComplianceCheck]: 'Compliance Check',
      [AuditType.HighValueAssets]: 'High Value Assets Audit',
      [AuditType.Reconciliation]: 'Reconciliation Audit',
      [AuditType.SpotCheck]: 'Spot Check Audit',
      [AuditType.Physical]: 'Physical Audit',
      [AuditType.Compliance]: 'Compliance Audit',
      [AuditType.Financial]: 'Financial Audit',
      [AuditType.Condition]: 'Condition Audit',
      [AuditType.Verification]: 'Verification Audit',
    };

    const scopeNames: Record<AuditScope, string> = {
      [AuditScope.AllAssets]: 'All Assets',
      [AuditScope.Locations]: 'Building A',
      [AuditScope.Departments]: 'IT Department',
      [AuditScope.Categories]: 'Computer Equipment',
      [AuditScope.CustomList]: 'Custom Selection',
    };

    return `${typeNames[type]} - ${scopeNames[scope]}`;
  }

  /**
   * Generate recurrence pattern
   */
  private generateRecurrencePattern(): RecurrencePattern {
    const frequencies: RecurrenceFrequency[] = [
      RecurrenceFrequency.Daily,
      RecurrenceFrequency.Weekly,
      RecurrenceFrequency.Monthly,
      RecurrenceFrequency.Quarterly,
      RecurrenceFrequency.Yearly
    ];
    const frequency = frequencies[Math.floor(Math.random() * frequencies.length)];

    return {
      frequency,
      interval: 1,
      endDate: this.randomFutureDate(365).toISOString(),
      occurrences: getRecurrenceOccurrences(frequency),
    };
  }

  /**
   * Generate 18 open + 45 resolved exceptions
   */
  private generateExceptions(audits: Audit[], assets: DemoAsset[], users: DemoUser[]): AuditException[] {
    const exceptions: AuditException[] = [];
    const activeAudits = audits.filter(a => a.status === AuditStatus.InProgress || a.status === AuditStatus.UnderReview);

    // Generate 18 open exceptions
    for (let i = 0; i < 18; i++) {
      exceptions.push(this.createException(i + 1, 'open', activeAudits, assets, users));
    }

    // Generate 45 resolved exceptions
    for (let i = 0; i < 45; i++) {
      exceptions.push(this.createException(i + 19, 'resolved', audits, assets, users));
    }

    return exceptions;
  }

  /**
   * Create a single exception
   */
  private createException(
    id: number,
    category: 'open' | 'resolved',
    audits: Audit[],
    assets: DemoAsset[],
    users: DemoUser[]
  ): AuditException {
    const types: ExceptionType[] = [
      ExceptionType.NotFound,
      ExceptionType.ConditionIssue,
      ExceptionType.LocationMismatch,
      ExceptionType.DataDiscrepancy,
      ExceptionType.MaintenanceRequired,
      ExceptionType.ComplianceIssue,
      ExceptionType.UnauthorizedMovement,
      ExceptionType.Other
    ];
    const severities: ExceptionSeverity[] = [
      ExceptionSeverity.Low,
      ExceptionSeverity.Medium,
      ExceptionSeverity.High,
      ExceptionSeverity.Critical
    ];
    const priorities: ExceptionPriority[] = [
      ExceptionPriority.Low,
      ExceptionPriority.Medium,
      ExceptionPriority.High,
      ExceptionPriority.Urgent
    ];

    const type = types[Math.floor(Math.random() * types.length)];
    const severity = severities[Math.floor(Math.random() * severities.length)];
    const priority = priorities[Math.floor(Math.random() * priorities.length)];

    const audit = audits[Math.floor(Math.random() * audits.length)];
    const asset = assets[Math.floor(Math.random() * assets.length)];
    const reporter = users[Math.floor(Math.random() * users.length)];

    const status: ExceptionStatus = category === 'open' 
      ? [ExceptionStatus.Open, ExceptionStatus.UnderReview, ExceptionStatus.ActionRequired][Math.floor(Math.random() * 3)]
      : ExceptionStatus.Resolved;

    const reportedDate = this.randomPastDate(30);
    const resolvedDate = category === 'resolved' ? this.randomDateBetween(reportedDate, new Date()) : undefined;

    return {
      id: `exception-${id}`,
      exceptionNumber: `EXC-${String(id).padStart(6, '0')}`,
      auditId: audit.id,
      auditNumber: audit.auditNumber,
      assetId: asset.id,
      assetCode: asset.code,
      assetName: asset.name,
      exceptionType: type,
      severity,
      priority,
      status,
      title: this.generateExceptionTitle(type, asset),
      description: this.generateExceptionDescription(type),
      expectedValue: this.generateExpectedValue(type, asset),
      actualValue: this.generateActualValue(type),
      reportedBy: reporter.id,
      reportedByName: reporter.name,
      reportedDate: reportedDate.toISOString(),
      resolvedDate: resolvedDate?.toISOString(),
      resolvedBy: resolvedDate ? users[0].id : undefined,
      resolvedByName: resolvedDate ? users[0].name : undefined,
      resolutionNotes: resolvedDate ? 'Issue has been resolved and verified.' : undefined,
      requiresFollowUp: category === 'open' && Math.random() > 0.5,
      attachments: Math.random() > 0.3 ? [
        {
          id: `img-${id}-1`,
          fileName: 'exception-photo.jpg',
          fileType: 'image/jpeg',
          fileSize: 1024000,
          url: `https://picsum.photos/800/600?random=${id}`,
          thumbnailUrl: `https://picsum.photos/200/150?random=${id}`,
          uploadedBy: reporter.id,
          uploadedByName: reporter.name,
          uploadedDate: reportedDate.toISOString(),
        },
      ] : [],
      comments: [],
      createdDate: reportedDate.toISOString(),
      lastModifiedDate: resolvedDate?.toISOString() || reportedDate.toISOString(),
    };
  }

  /**
   * Generate exception title
   */
  private generateExceptionTitle(type: ExceptionType, asset: DemoAsset): string {
    const titles: Record<ExceptionType, string> = {
      [ExceptionType.NotFound]: `Asset Not Found: ${asset.code}`,
      [ExceptionType.ConditionIssue]: `Condition Issue: ${asset.name}`,
      [ExceptionType.LocationMismatch]: `Location Discrepancy: ${asset.code}`,
      [ExceptionType.DataDiscrepancy]: `Data Mismatch: ${asset.code}`,
      [ExceptionType.MaintenanceRequired]: `Maintenance Required: ${asset.name}`,
      [ExceptionType.ComplianceIssue]: `Compliance Issue: ${asset.code}`,
      [ExceptionType.UnauthorizedMovement]: `Unauthorized Movement: ${asset.code}`,
      [ExceptionType.DuplicateAsset]: `Duplicate Asset: ${asset.code}`,
      [ExceptionType.Other]: `Exception: ${asset.code}`,
      [ExceptionType.AssetMissing]: `Asset Missing: ${asset.code}`,
      [ExceptionType.AssetDamaged]: `Asset Damaged: ${asset.name}`,
      [ExceptionType.ConditionMismatch]: `Condition Mismatch: ${asset.code}`,
    };
    return titles[type];
  }

  /**
   * Generate exception description
   */
  private generateExceptionDescription(type: ExceptionType): string {
    const descriptions: Record<ExceptionType, string> = {
      [ExceptionType.NotFound]: 'Asset could not be located at the expected location during physical verification.',
      [ExceptionType.ConditionIssue]: 'Current condition does not match the condition recorded in the system.',
      [ExceptionType.LocationMismatch]: 'Asset found at a different location than recorded in the system.',
      [ExceptionType.DataDiscrepancy]: 'Asset information does not match system records.',
      [ExceptionType.MaintenanceRequired]: 'Asset requires maintenance that was not previously scheduled.',
      [ExceptionType.ComplianceIssue]: 'Asset does not meet compliance requirements.',
      [ExceptionType.UnauthorizedMovement]: 'Asset has been moved without proper authorisation.',
      [ExceptionType.DuplicateAsset]: 'Duplicate asset entry detected in the system.',
      [ExceptionType.Other]: 'Other exception requiring review.',
      [ExceptionType.AssetMissing]: 'Asset is missing from the expected location.',
      [ExceptionType.AssetDamaged]: 'Asset has been damaged and requires attention.',
      [ExceptionType.ConditionMismatch]: 'Asset condition does not match expected condition.',
    };
    return descriptions[type];
  }

  /**
   * Generate expected value for exception
   */
  private generateExpectedValue(type: ExceptionType, asset: DemoAsset): string {
    if (type === ExceptionType.LocationMismatch) return asset.location;
    if (type === ExceptionType.ConditionIssue) return asset.condition;
    if (type === ExceptionType.DataDiscrepancy) return asset.name;
    return 'Present';
  }

  /**
   * Generate actual value for exception
   */
  private generateActualValue(type: ExceptionType): string {
    if (type === ExceptionType.NotFound) return 'Not Found';
    if (type === ExceptionType.LocationMismatch) return 'Building B - Floor 1';
    if (type === ExceptionType.ConditionIssue) return AssetCondition.Poor;
    if (type === ExceptionType.DataDiscrepancy) return 'Different Name';
    if (type === ExceptionType.MaintenanceRequired) return 'Maintenance Needed';
    return 'Exception Detected';
  }

  /**
   * Generate activity logs for audits
   */
  private generateActivityLogs(audits: Audit[], users: DemoUser[]): AuditActivityLog[] {
    const logs: AuditActivityLog[] = [];
    let logId = 1;

    // Generate 3-8 logs per active/completed audit
    audits
      .filter(a => a.status !== AuditStatus.Scheduled && a.status !== AuditStatus.Cancelled)
      .forEach(audit => {
        const numLogs = Math.floor(Math.random() * 6) + 3;
        for (let i = 0; i < numLogs; i++) {
          logs.push(this.createActivityLog(logId++, audit, users));
        }
      });

    return logs;
  }

  /**
   * Create a single activity log
   */
  private createActivityLog(id: number, audit: Audit, users: DemoUser[]): AuditActivityLog {
    const types: AuditActivityType[] = [
      AuditActivityType.Created,
      AuditActivityType.Started,
      AuditActivityType.StatusChanged,
      AuditActivityType.UserAdded,
      AuditActivityType.UserRemoved,
      AuditActivityType.AssetAudited,
      AuditActivityType.ExceptionRaised,
      AuditActivityType.ExceptionResolved,
      AuditActivityType.CommentAdded,
      AuditActivityType.Completed,
    ];

    const type = types[Math.floor(Math.random() * types.length)];
    const user = users[Math.floor(Math.random() * users.length)];
    
    // Use actualStartDate if available, otherwise use createdDate, fallback to scheduledStartDate
    const startDate = this.getAuditStartDate(audit);
    
    const timestamp = this.randomDateBetween(startDate, new Date());

    return {
      id: `log-${id}`,
      auditId: audit.id,
      activityType: type,
      description: this.generateActivityDescription(type, user.name),
      userId: user.id,
      userName: user.name,
      performedBy: user.name,
      timestamp: timestamp.toISOString(),
      metadata: {},
    };
  }

  /**
   * Generate activity description
   */
  private generateActivityDescription(type: AuditActivityType, userName: string): string {
    const descriptions: Record<AuditActivityType, string> = {
      [AuditActivityType.Created]: `Audit created by ${userName}`,
      [AuditActivityType.Assigned]: `Audit assigned by ${userName}`,
      [AuditActivityType.Started]: `Audit started by ${userName}`,
      [AuditActivityType.AssetAudited]: `Asset audited by ${userName}`,
      [AuditActivityType.ExceptionRaised]: `Exception raised by ${userName}`,
      [AuditActivityType.ExceptionResolved]: `Exception resolved by ${userName}`,
      [AuditActivityType.CommentAdded]: `Comment added by ${userName}`,
      [AuditActivityType.StatusChanged]: `Status changed by ${userName}`,
      [AuditActivityType.UserAdded]: `${userName} was assigned to the audit`,
      [AuditActivityType.UserRemoved]: `${userName} was removed from the audit`,
      [AuditActivityType.Completed]: `Audit completed by ${userName}`,
      [AuditActivityType.Cancelled]: `Audit cancelled by ${userName}`,
      [AuditActivityType.Rescheduled]: `Audit rescheduled by ${userName}`,
      [AuditActivityType.Paused]: `Audit paused by ${userName}`,
      [AuditActivityType.Resumed]: `Audit resumed by ${userName}`,
    };
    return descriptions[type] || `Activity by ${userName}`;
  }

  // Utility functions

  // Helper function to get audit start date - extracted to fix nested ternary (S3358)
  private getAuditStartDate(audit: Audit): Date {
    if (audit.actualStartDate) {
      return new Date(audit.actualStartDate);
    }
    if (audit.createdDate) {
      return new Date(audit.createdDate);
    }
    if (audit.scheduledStartDate) {
      return new Date(audit.scheduledStartDate);
    }
    return new Date();
  }

  private randomPastDate(maxDaysAgo: number, minDaysAgo: number = 0): Date {
    const now = Date.now();
    const min = minDaysAgo * 24 * 60 * 60 * 1000;
    const max = maxDaysAgo * 24 * 60 * 60 * 1000;
    return new Date(now - min - Math.random() * (max - min));
  }

  private randomFutureDate(maxDaysAhead: number): Date {
    const now = Date.now();
    const max = maxDaysAhead * 24 * 60 * 60 * 1000;
    return new Date(now + Math.random() * max);
  }

  private randomDateBetween(start: Date, end: Date): Date {
    // Safety check for invalid dates
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
      console.warn('Invalid dates passed to randomDateBetween, using current date');
      return new Date();
    }
    
    const startTime = start.getTime();
    const endTime = end.getTime();
    
    // Ensure start is before end
    if (startTime > endTime) {
      return new Date(endTime + Math.random() * (startTime - endTime));
    }
    
    return new Date(startTime + Math.random() * (endTime - startTime));
  }

  private shuffleArray<T>(array: T[]): T[] {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  // Storage functions

  private loadFromStorage(): DemoData | null {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const data = JSON.parse(stored);
      
      // Keep dates as strings (ISO format) - the Audit interface expects strings
      // Only convert lastGenerated for internal use
      data.lastGenerated = new Date(data.lastGenerated);

      return data;
    } catch (error) {
      console.error('Failed to load demo data from storage:', error);
      return null;
    }
  }

  private saveToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.data));
    } catch (error) {
      console.error('Failed to save demo data to storage:', error);
    }
  }
}

// Export singleton instance
export const demoDataService = new DemoDataService();
